import React from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import assign from 'lodash/assign';
import result from 'lodash/result';
import isFunction from 'lodash/isFunction';
import isNumber from 'lodash/isNumber';
import filter from 'lodash/filter';
import moment from 'moment';
import Cookie from 'react-cookie';
import { TimeMap } from '../data/time-data';
import { TimeUtils } from '../data/time-utils';
import { FormattedMessage, injectIntl } from 'react-intl';

const searchByDateTimeHOC = (Component) => {
  class HOC extends React.Component {
    constructor (props) {
      super(props);
      this.state = {
        startTimeIndex: '',
        endTimeIndex: '',
        formError: '',
        selectedDate: null,
        isStartTimeOptional: true,
        isEndTimeOptional: true,
        displayDateTime: ''
      };
      this.selectStartTime = this.selectStartTime.bind(this);
      this.selectEndTime = this.selectEndTime.bind(this);
      this.onDayClick = this.onDayClick.bind(this);
      this.clearFilter = this.clearFilter.bind(this);
      this.submitForm = this.submitForm.bind(this);
      this.submitFormCb = this.submitFormCb.bind(this);
      this.onHideFn = this.onHideFn.bind(this);
      this.inputRefs = {};
    }

    componentDidMount () {
      const getDateTime = get(this.props, 'selectedFilters.availability', '');
      if (getDateTime) {
        // Split string to ISO strings for start time and end time ex: [2018-11-15T09:30:00, 2018-11-15T12:00:00]
        // Convert to object ex: { date: 15,  hours: 6, milliseconds: 0, minutes: 0, months: 10, seconds: 0, years: 2018 }
        // Filter in TimeMap for time index that matches the hours and minutes,
        // there will sometimes be two items in the array that gets returned, overnight time and non overnight.
        // ex: { 36: { time_index: 36, time: '12:00 am*', h: 0, m: 0, overnight: true } }
        // For end times, filter for overnight values, by comparing if end date is after start date
        const dateTime = getDateTime.split(',');
        const start = dateTime[2];
        const end = dateTime[3];
        const momentStart = moment(start);
        const momentEnd = moment(end);
        const overnight = momentEnd.isAfter(start, 'date');
        const startObject = momentStart.toObject();
        const endObject = momentEnd.toObject();
        const startTimeObj = filter(TimeMap, { h: startObject.hours, m: startObject.minutes })[0] || {};
        const endTimeObj = filter(TimeMap, { h: endObject.hours, m: endObject.minutes, overnight })[0] || {};
        const displayDateTime = this.displayDateTime(momentStart, startTimeObj, endTimeObj);
        const startHash = TimeUtils.scheduleHash('start', start, startTimeObj.time_index);
        const endHash = TimeUtils.scheduleHash('end', end, endTimeObj.time_index);
        const hash = assign({}, startHash, endHash);
        if (startHash) {
          // 24hrs
          const expires = new Date((Date.now()) + (1000 * 60 * 60 * 24));
          Cookie.save('booking_schedule', [hash], { path: '/', expires });
        }

        this.setState({
          startTimeIndex: startTimeObj.time_index,
          endTimeIndex: endTimeObj.time_index,
          selectedDate: momentStart.toDate(),
          displayDateTime
        });
      }
    }

    onDayClick (date) {
      this.setState({ selectedDate: date });
    }

    onHideFn () {
      this.setState({ formError: '' });
    }

    formatIsoString (momentDate, startTime, endTime) {
      // convert moment date to object format
      // if there are no start and end times default to start at 6am to 6am next day
      // finally convert start and end date/times to ISO string
      const ISOFormat = 'YYYY-MM-DDTHH:mm:ss';
      const startDateObject = momentDate.toObject();
      const endDateObject = (!startTime.h && !endTime.h) || endTime.overnight ? momentDate.add(1, 'd').toObject() : momentDate.toObject();
      const startDateTime = assign({}, startDateObject, { hours: isNumber(startTime.h) ? startTime.h : 6, minutes: startTime.m || 0 });
      const endDateTime = assign({}, endDateObject, { hours: isNumber(endTime.h) ? endTime.h : 6, minutes: endTime.m || 0 });
      return {
        startDate: moment(startDateTime).format(ISOFormat),
        endDate: moment(endDateTime).format(ISOFormat)
      };
    }

    displayDateTime (momentDate, startTime, endTime) {
      const { intl } = this.props;
      let timeString = '';
      if (startTime && startTime.time && endTime && endTime.time){
        timeString+= intl.formatTime(moment(startTime.time, 'LT').format()) + ' - ' + intl.formatTime(moment(endTime.time, 'LT').format());;
      }
      const displayDateTime = `${intl.formatDate(momentDate, { month: 'short', day: 'numeric' })} ${timeString}`;
      return displayDateTime;
    }

    submitForm (element, callback, refs, value) {
      return callback(value);
    }

    submitFormCb (value) {
      // if there is no date present in the value, then we show an error message
      if (!value.dateInput) return this.setState({ formError: <FormattedMessage id="search.search_date_time_hoc.empty_date" defaultMessage="Please select a date." /> });
      // check to see if we have lat and lng defined, otherwise display error
      if (window.map) {
        const lat = result(window.map || {}, 'center.lat');
        const lng = result(window.map || {}, 'center.lng');
        if (!lat || !lng) {
          return this.setState({ formError: <FormattedMessage id="search.search_date_time_hoc.filter_error" defaultMessage="We encountered an error with this filter." /> });
        }
      }
      // Find the time map that matches the index of the start time, or default to empty object
      const startTime = TimeMap[value.startTimeIndex] || {};
      // Find the time map that matches the index of the end time, or default to empty object
      const endTime = TimeMap[value.endTimeIndex] || {};
      const momentDate = moment(new Date(value.dateInput));
      const IsoDateString = this.formatIsoString(momentDate.clone(), startTime, endTime); // clone to avoid mutation
      const { endDate, startDate } = IsoDateString;
      const startObj = TimeUtils.scheduleHash('start', startDate, value.startTimeIndex) || {}; // start hash
      const endObj = TimeUtils.scheduleHash('end', endDate, value.endTimeIndex) || {}; // end hash
      const startEndHash = assign({}, startObj, endObj);
      // We load the booking_schedule cookie to see if it is the user's first time updating their schedule. For analytics
      const schedule = Cookie.load('booking_schedule');
      const duration = moment.duration(moment(endDate).diff(moment(startDate))).as('hours');
      // analyticsIntegrations.searchByDate('search page filter', startObj.start, endObj.end, duration, !schedule);

      const displayDateTime = this.displayDateTime(momentDate.clone(), startTime, endTime);
      this.setState({ displayDateTime, formError: '', selectedDateString: `${startDate},${endDate}` });
      if (isFunction(this.props.filterResults)) {
        this.props.filterResults({ availability_interval: `${startDate},${endDate}` });
      }

      let expires;
      if (startObj.start) {
        // 24hrs
        expires = new Date((Date.now()) + (1000 * 60 * 60 * 24));
      }
      // Save schedule to a cookie so that it populates schedule in listing page
      Cookie.save('booking_schedule', [startEndHash], { path: '/', expires });

      if (isFunction(this.props.beforeModalCloseFn)) {
        this.props.beforeModalCloseFn();
      }
      return this.formComponent.close();
    }

    selectStartTime (value, closeDropdownCb) {
      this.setState({
        startTimeIndex: value,
        isEndTimeOptional: false
      });
      closeDropdownCb();
    }

    selectEndTime (value, closeDropdownCb) {
      this.setState({
        endTimeIndex: value,
        isStartTimeOptional: false
      });
      closeDropdownCb();
    }

    clearFilter () {
      this.setState({
        startTimeIndex: '',
        endTimeIndex: '',
        selectedDate: null,
        selectedDateString: '',
        isStartTimeOptional: true,
        isEndTimeOptional: true,
        formError: '',
        displayDateTime: ''
      });
      const schedule = Cookie.load('booking_schedule');
      // analyticsIntegrations.searchByDate('search page filter', null, null, null, !schedule);
      if (isFunction(this.props.filterResults)) {
        return this.props.filterResults({ availability_interval: '' });
      }
    }

    render () {
      const { hasError, ...restProps } = this.props;
      return (
        <Component
          {...restProps}
          {...this.state}
          ref={ref => { this.formComponent = ref; }}
          onHideFn={this.onHideFn}
          submitForm={this.submitForm}
          submitFormCb={this.submitFormCb}
          clearFilter={this.clearFilter}
          selectStartTime={this.selectStartTime}
          selectEndTime={this.selectEndTime}
          onDayClick={this.onDayClick}
        />
      );
    }
  }
  HOC.propTypes = {
    filterResults: PropTypes.func,
    beforeModalCloseFn: PropTypes.func,
    hasError: PropTypes.bool
  };
  return injectIntl(HOC);
};

export default searchByDateTimeHOC;
